import type { SxProps, Theme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import { ReactNode } from 'react';

import { UploadIllustration } from 'src/assets/illustrations';
import { createClasses } from 'src/theme/create-classes';

import { mergeClasses } from '@src/utils';

export type UploadPlaceholderProps = React.ComponentProps<'div'> & {
  uploadTitle?: ReactNode;
  uploadDescription?: ReactNode;
  sx?: SxProps<Theme>;
};

const uploadPlaceholderClasses = {
  root: createClasses('upload__placeholder__root'),
  content: createClasses('upload__placeholder__content'),
  title: createClasses('upload__placeholder__title'),
  description: createClasses('upload__placeholder__description'),
};

const PlaceholderRoot = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  justifyContent: 'center',
}));

const PlaceholderContent = styled('div')(({ theme }) => ({
  display: 'flex',
  textAlign: 'center',
  gap: theme.spacing(1),
  flexDirection: 'column',
  [`& .${uploadPlaceholderClasses.title}`]: { ...theme.typography.h6 },
  [`& .${uploadPlaceholderClasses.description}`]: {
    ...theme.typography.body2,
    color: theme.vars.palette.text.secondary,
    '& span': {
      textDecoration: 'underline',
      margin: theme.spacing(0, 0.5),
      color: theme.vars.palette.primary.main,
    },
  },
}));

export function UploadPlaceholder({ sx, uploadTitle, uploadDescription, className, ...other }: UploadPlaceholderProps) {
  const title = uploadTitle || 'Select file';
  const description = uploadDescription || (
    <>
      Drop file here or click to <span>browse</span> through your machine
    </>
  );
  return (
    <PlaceholderRoot className={mergeClasses([uploadPlaceholderClasses.root, className])} sx={sx} {...other}>
      <UploadIllustration hideBackground sx={{ width: 200 }} />
      <PlaceholderContent>
        <div className={uploadPlaceholderClasses.title}>{title}</div>
        <div className={uploadPlaceholderClasses.description}>{description}</div>
      </PlaceholderContent>
    </PlaceholderRoot>
  );
}
